<template>
  <v-dialog
    v-model="dialog"
    persistent
    max-width="800px"
    content-class="modern-dialog"
  >
    <v-card class="modern-card">
      <v-card-title
        class="d-flex justify-space-between align-center px-6 pt-6 pb-4"
      >
        <div class="text-h5 font-weight-bold primary--text">
          Select Your Causes
        </div>
        <v-btn icon @click="onClose" class="close-btn">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="px-6 pb-2 text-body-1 secondary--text">
        What types of causes are you passionate about?
      </v-card-text>

      <v-card-actions class="px-6 pb-4">
        <v-btn
          @click="selectAll"
          outlined
          color="primary"
          class="select-btn rounded-pill"
          :class="{ 'all-selected': allSelected }"
        >
          <v-icon left>{{
            allSelected ? "mdi-heart" : "mdi-heart-outline"
          }}</v-icon>
          {{ allSelected ? "All Selected" : "Select All" }}
        </v-btn>
      </v-card-actions>

      <v-card-text class="px-4 pb-0">
        <v-row>
          <v-col
            cols="6"
            sm="4"
            md="3"
            v-for="item in causes"
            :key="item.name"
            class="pa-2"
          >
            <v-hover v-slot="{ hover }">
              <v-card
                @click="selectCause(item)"
                :class="{
                  'selected-card': item.selected,
                  'hover-card': hover,
                  'elevated-card': item.selected || hover
                }"
                class="cause-card rounded-xl"
                :elevation="item.selected || hover ? 6 : 2"
              >
                <v-card-text
                  class="d-flex flex-column align-center justify-center py-4 px-2"
                >
                  <div class="icon-wrapper mb-3">
                    <site-icon
                      :name="item.name"
                      type="Cause"
                      :size="$vuetify.breakpoint.xs ? '40' : '48'"
                      :color="item.selected ? 'primary' : 'secondary'"
                    />
                  </div>
                  <span
                    class="text-center font-weight-medium"
                    :class="item.selected ? 'primary--text' : 'secondary--text'"
                  >
                    {{ item.name }}
                  </span>
                </v-card-text>

                <!-- Heart icon for selected items -->
                <div class="heart-indicator" v-if="item.selected">
                  <v-icon color="primary" size="24">mdi-heart</v-icon>
                </div>
              </v-card>
            </v-hover>
          </v-col>
        </v-row>
      </v-card-text>

      <v-card-text v-if="message" class="px-6 py-0">
        <v-alert
          dense
          type="error"
          v-text="message"
          class="rounded-lg mt-4 mb-0"
        />
      </v-card-text>

      <v-card-actions class="px-6 pb-6 pt-4 d-flex justify-center">
        <v-btn
          color="#b5e539"
          @click="onSave"
          width="200"
          :loading="loading"
          class="save-btn rounded-pill"
          depressed
          large
        >
          <span class="font-weight-bold">Save</span>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapActions, mapGetters, mapState } from "vuex";
import SiteIcon from "@/components/common/SiteIcon.vue";

export default {
  components: { SiteIcon },
  props: {
    dialog: {
      type: Boolean,
      default: false
    },
    onClose: {
      type: Function
    }
  },
  data() {
    return {
      message: null,
      loading: false,
      causes: []
    };
  },
  computed: {
    ...mapState("auth", {
      profile: "profile"
    }),
    ...mapGetters({
      allCauses: "icon/causes"
    }),
    allSelected() {
      return this.causes.length > 0 && this.causes.every(c => c.selected);
    }
  },
  methods: {
    ...mapActions({
      updateProfile: "auth/updateProfile",
      getCauseIcons: "icon/getCauseIcons"
    }),
    onChange() {
      this.message = null;
    },
    onSave() {
      const selectedCauses = this.causes
        .filter(c => c.selected)
        .map(c => c.name);
      if (selectedCauses.length === 0) {
        this.message = "Please select at least one cause to continue.";
        return;
      }

      this.loading = true;
      this.updateProfile({ causesStr: selectedCauses.join() })
        .then(() => {
          this.loading = false;
          this.onClose();
        })
        .catch(error => {
          this.loading = false;
          this.message =
            error.response?.data?.message ||
            "An error occurred. Please try again.";
        });
    },
    selectCause(item) {
      item.selected = !item.selected;
      this.causes = [...this.causes];
      this.message = null;
    },
    selectAll() {
      const shouldSelect = !this.allSelected;
      this.causes.forEach(c => (c.selected = shouldSelect));
      this.causes = [...this.causes];
      this.message = null;
    }
  },
  watch: {
    "profile.causes": function(newValue) {
      if (newValue) {
        for (var i = 0; i < newValue.length; i++) {
          for (var j = 0; j < this.causes.length; j++) {
            if (newValue[i] == this.causes[j].name) {
              this.causes[j].selected = true;
              break;
            }
          }
        }
        this.causes = [...this.causes];
      }
    },
    allCauses(newValue) {
      this.causes = [...newValue].sort((a, b) => a.name.localeCompare(b.name));
      if (this.profile && this.profile.causes) {
        for (var i = 0; i < this.profile.causes.length; i++) {
          for (var j = 0; j < this.causes.length; j++) {
            if (this.profile.causes[i] == this.causes[j].name) {
              this.causes[j].selected = true;
              break;
            }
          }
        }
        this.causes = [...this.causes];
      }
    }
  },
  mounted() {
    this.causes = [...this.allCauses].sort((a, b) => a.name.localeCompare(b.name));
    if (this.profile && this.profile.causes) {
      for (var i = 0; i < this.profile.causes.length; i++) {
        for (var j = 0; j < this.causes.length; j++) {
          if (this.profile.causes[i] == this.causes[j].name) {
            this.causes[j].selected = true;
            break;
          }
        }
      }
      this.causes = [...this.causes];
    }
    if (!this.allCauses.length) {
      this.getCauseIcons().catch(error => console.log(error));
    }
  }
};
</script>

<style scoped>
.modern-dialog {
  border-radius: 16px;
  overflow: hidden;
}

.modern-card {
  border-radius: 16px !important;
  background: linear-gradient(to bottom, #ffffff, #f8f9fa);
}

.close-btn {
  transition: all 0.3s ease;
}

.close-btn:hover {
  transform: rotate(90deg);
  color: var(--v-error-base) !important;
}

.cause-card {
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
  border: 2px solid transparent;
  position: relative;
}

.cause-card:hover {
  transform: translateY(-4px);
}

.selected-card {
     border-color: #3a7bd5 !important;
  background-color: rgba(10, 91, 138, 0.05) !important;
}

.hover-card {
    border-color: #3a7bd5 !important;
}

.icon-wrapper {
  position: relative;
}

.heart-indicator {
  position: absolute;
  top: 8px;
  right: 8px;
  background: white;
  border-radius: 50%;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.select-btn {
  transition: all 0.3s ease;
  letter-spacing: 0.5px;
}

.select-btn.all-selected {
  background-color: rgba(10, 91, 138, 0.1) !important;
}

.save-btn {
  background-color: #b5e539 !important;
  box-shadow: 0 4px 12px rgba(181, 229, 57, 0.3);
  transition: all 0.3s ease;
}

.save-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(181, 229, 57, 0.4);
}

.save-btn:active {
  transform: translateY(0);
}

@media (max-width: 600px) {
  .modern-dialog {
    margin: 16px;
  }

  .cause-card {
    padding: 8px !important;
  }

  .heart-indicator {
    top: 4px;
    right: 4px;
    width: 24px;
    height: 24px;
  }
}

/* Custom scrollbar for dialog */
::-webkit-scrollbar {
  width: 6px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb {
  background: var(--v-primary-lighten1);
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: var(--v-primary-base);
}
</style>
